<template>
  <BModal
    id="customer-search-form"
    ref="customerSearchModal"
    :title="translatedLabel('formTitle', 'mymachine_customerSerialForm')"
    no-close-on-backdrop
  >
    <div class="px-3 px-md-5 customer-search">
      <p>{{ translate('formDescription', labels, 'mymachine_customerSerialForm') }}</p>
      <div class="d-flex justify-content-center mb-3">
        <div class="w-100">
          <p class="search-by">{{ searchBy }}<span class="search-by-error">*</span></p>
          <p class="search-by-error">{{ searchByRequired }}</p>
          <BasicFilterItem
            id="customer-type"
            ref="customerTypeRef"
            class="wide dropdown m-0"
            :disabled="loading || searchTypes.length === 1"
            :title="selectedCustomerType"
            :class="{'is-invalid': false, 'is-placeholder': customerType === ''}"
          >
            <div
              v-for="(itm, i) in searchTypes"
              :key="'customer_item_' + i"
              class="nav-item text-nowrap"
              :class="{'disabled': isDisabledOption(itm)}"
              @click="hideSearchDropdown"
            >
              <BDropdownItem @click="selectCustomerType(itm.label)">{{ itm.label }}</BDropdownItem>
            </div>
          </BasicFilterItem>
          <SearchInput
            v-model="selectedCustomer"
            class="flex-grow-1 error-input mt-3"
            ajax
            submit-on-close
            :keep-button="false"
            :empty-label="translatedLabel('searchButton', 'mymachine_customerSerialForm')"
            :search-label="translatedLabel('searchButton', 'mymachine_customerSerialForm')"
            :placeholder="translatedLabel('enterYourErrorCode', 'mymachine_customerSerialForm')"
            @search="handleSearch"
          />
          <div v-if="isVisible">
            <ul
              v-if="query && results.length > 0"
              class="search-list"
            >
              <li
                v-for="(result, index) in results"
                :key="`${result.customerMdmId} - ${index}`"
                @click="handleClick(result)"
              >
                <span v-html="formattedResults[index]"></span>
              </li>
            </ul>
            <div
              v-else-if="query"
              class="no-result"
            >
              {{ searchErrorMessage }}
            </div>
          </div>
        </div>
      </div>
      <div
        v-if="isLoading"
        class="d-flex align-items-center justify-content-center mb-3"
      >
        <BSpinner
          variant="warning"
          small
          class="me-2"
        />
      </div>
      <div class="datatable">
        <p class="registered">{{ translate('registeredSerialNumber', labels, 'mymachine_customerSerialForm') }}</p>
        <p class="results">{{ equipmentsList.all.length }} {{ translate('results', labels, 'mymachine_customerSerialForm') }}</p>
        <p class="serial-found">{{ serialFound }}</p>
        <template v-if="equipmentsList.all.length != 0">
          <BasicGridTable
            :disabled="disabled"
            header-class="d-md-grid"
            grid-name="serial-fields"
          >
            <template #header>
              <BasicGridTableHeader
                v-for="f of fields"
                :key="'item__' + f.key"
                :name="f.key"
              >
                {{ f.label }}
              </BasicGridTableHeader>
            </template>
            <template #rows>
              <div
                v-for="(itm, i) of equipmentsList.displayed"
                :key="'item__' + i + itm.model"
                class="grid-table-row"
              >
                <div class="item__Selection font-condensed">
                  <div class="form-check dp-radio">
                    <input
                      :id="'radio_' + i + '_' + itm.serialNumber"
                      v-model="selectedOption"
                      class="form-check-input"
                      type="radio"
                      :value="itm.serialNumber"
                      name="radio-group"
                    />
                    <label
                      class="form-check-label"
                      :for="'radio_' + i + '_' + itm.serialNumber"
                    ></label>
                  </div>
                </div>
                <div class="item__Model font-condensed">{{ itm.model }}</div>
                <div class="item__SerialNumber text-small">{{ itm.serialNumber }}</div>
              </div>
            </template>
          </BasicGridTable>

          <div
            v-if="equipmentsList.all.length > 5"
            class="pagination-data"
          >
            <BPagination
              v-model="currentPage"
              align="center"
              class="col-12 col-sm-auto order-0 order-sm-1 mb-2 mb-sm-0 pe-0"
              first-number
              last-number
              hide-goto-end-buttons
              :total-rows="total"
              :per-page="perPage"
            >
              <template v-slot:prev-text>
                <b-icon :icon="['far', 'chevron-left']" />
              </template>

              <template v-slot:next-text>
                <b-icon :icon="['far', 'chevron-right']" />
              </template>

              <template v-slot:ellipsis-text>
                <div class="d-flex align-items-center justify-content-center fw-bold text-center">
                  &#x2026;
                </div>
              </template>
            </BPagination>
          </div>
        </template>
        <template v-else>
          <div class="searching-data">
            <b-icon
              icon="info-circle"
              class="info-circle"
            />
            <p class="search-term">{{ translate('searchTerm', labels, 'mymachine_customerSerialForm') }}</p>
          </div>
        </template>
      </div>
    </div>
    <template #footer>
      <div class="text-end d-flex flex-wrap d-md-block w-100">
        <BButton
          class="mt-2 ms-md-3 order-1 col-12 col-md-auto"
          variant="outline-primary"
          @click="closeModal"
        >
          {{ translate('cancelButton', labels, 'mymachine_customerSerialForm') }}
        </BButton>
        <BButton
          type="submit"
          :disabled="equipmentsList.all.length == 0 || loading"
          variant="primary"
          class="mt-2 ms-md-3 order-0 col-12 col-md-auto d-inline-flex justify-content-center align-items-center"
          @click="selectSerialNumber"
        >
          <BSpinner
            v-if="loading"
            small
            class="me-2"
          />
          {{ translate('submitButton', labels, 'mymachine_customerSerialForm') }}
          <b-icon
            icon="bobcaticon-create"
            class="small ms-1"
          />
        </BButton>
      </div>
    </template>
  </BModal>
</template>

<script setup lang="ts">
import {ref, computed, watch, onMounted, reactive, nextTick, useTemplateRef} from 'vue'
import {useStore} from 'vuex'
import SearchInput from '@/components/ui/forms/SearchInput.vue'
import BasicFilterItem from '@/components/ui/forms/BasicFilterItem.vue'
import BasicGridTable from '@/components/ui/BasicGridTable.vue'
import BasicGridTableHeader from '@/components/ui/BasicGridTableHeader.vue'
import {dpmmService} from '@/services'
import {useCommon} from '@/composables/useCommon'
import debounce from 'lodash.debounce'

const {labels, translate, translatedLabel} = useCommon()
const store = useStore()

withDefaults(defineProps<{
  disabled?: boolean
}>(), {
  disabled: false
})

const emit = defineEmits<{(e: 'serialNumber', sn: Record<string, any>): void
}>()

const customerSearchModal = useTemplateRef('customerSearchModal')
const customerTypeRef = useTemplateRef('customerTypeRef')

const isVisible = ref(true)
const loading = ref(false)
const _open = ref(false)
const isLoading = ref(false)
const total = ref(0)
const perPage = ref(5)
const currentPage = ref(1)
const selectedOption = ref('')
const searchBy = ref('')
const searchByRequired = ref('')
const selectedType = ref('')
const query = ref('')
const customerType = ref('')
const selectedCustomer = ref('')
const serialFound = ref('')
const searchErrorMessage = ref('')
const equipmentsList = reactive<{ all: any[]; displayed: any[] }>({
  all: [],
  displayed: []
})
const results = ref<any[]>([])

const _getRegion = computed<string>(() => store.getters['mymachine/getRegion'])
const _getSerials = computed(() => store.getters['mymachine/getSerials'])
const _getCurrentSerial = computed<string>(() => store.getters['mymachine/getCurrentSerial'])
const _getCurrentModel = computed<string>(() => store.getters['mymachine/getCurrentModel'])
const _getAppReady = computed<boolean>(() => store.getters['mymachine/getAppReady'])

const fields = computed(() => [
  {key: 'radioOption', label: translate('radioOption', labels.value, 'mymachine_customerSerialForm'), sortable: false},
  {key: 'model', label: translate('model', labels.value, 'mymachine_customerSerialForm'), sortable: false},
  {key: 'serialNumber', label: translate('serialNumber', labels.value, 'mymachine_customerSerialForm'), sortable: false}
])

const searchTypes = computed(() => [
  {name: 'CUSTOMER_ACCOUNT_NAME', label: translate('companyName', labels.value, 'mymachine_customerSerialForm')},
  {name: 'CUSTOMER_NAME', label: translate('customerFirstLastName', labels.value, 'mymachine_customerSerialForm')}
])

const selectedCustomerType = computed<string>(() => customerType.value || translatedLabel('customerType', 'mymachine_customerSerialForm'))

const formattedResults = computed<string[]>(() => {
  const highlight = (value: string): string => {
    if (!value || typeof value !== 'string') return value
    const words = query.value.split(' ').filter((word: string) => word.trim())
    const regex = new RegExp(`(${words.join('|')})`, 'gi')
    return value.replace(regex, (match: string) => `<span class="highlight">${match}</span>`)
  }
  return results.value.map((item: any) => {
    const accountName = item.accountName || ''
    const firstName = item.firstName || ''
    const lastName = item.lastName || ''
    const city = item.city || ''
    const country = item.country || ''
    const locationPart = city || country ? ` - ${city}, ${country}` : ''
    return `${highlight(accountName)} - ${highlight(firstName)} ${highlight(lastName)}${locationPart}`
  })
})

watch(currentPage, (page) => {
  equipmentsList.displayed = equipmentsList.all.slice((page - 1) * perPage.value, page * perPage.value)
})

watch(perPage, () => {
  currentPage.value = 1
  equipmentsList.displayed = equipmentsList.all.slice(0, perPage.value)
})

watch(() => equipmentsList.all, () => {
  currentPage.value = 1
})

onMounted(() => {
  searchBy.value = translatedLabel('searchBy', 'mymachine_customerSerialForm')
  searchErrorMessage.value = translatedLabel('noCustomers', 'mymachine_customerSerialForm')
})

function hideSearchDropdown(): void {
  customerTypeRef.value?.hideDropdown()
}

function isDisabledOption(item: unknown): boolean {
  return typeof item === 'object' && item !== null && 'disabled' in item && !!(item as Record<string, unknown>).disabled
}

function openModal(): void {
  reuseData()
  customerSearchModal.value?.show()
}

function reuseData(): void {
  isVisible.value = true
  isLoading.value = false
  selectedOption.value = ''
  serialFound.value = ''
  equipmentsList.all = []
  equipmentsList.displayed = []
  results.value = []
  selectedCustomer.value = ''
  searchByRequired.value = ''
}

const handleSearch = debounce(function (queryVal: string) {
  isVisible.value = true
  equipmentsList.all = []
  equipmentsList.displayed = []
  query.value = queryVal
  if (customerType.value) {
    if (queryVal.length > 2) {
      searchErrorMessage.value = ''
      isLoading.value = !isLoading.value
      const match = searchTypes.value.find((item: any) => item['label'] === customerType.value)
      selectedType.value = match!['name']
      dpmmService.searchByCustomer(selectedType.value, query.value).then((r: any) => {
        if (r.error_code) {
          isLoading.value = false
          searchErrorMessage.value = r.error_message
          results.value = []
        } else {
          isLoading.value = false
          searchErrorMessage.value = ''
          results.value = r
        }
      }).catch(() => {
        isLoading.value = false
      })
    } else {
      searchByRequired.value = ''
      isLoading.value = false
      searchErrorMessage.value = translatedLabel('enterCharacters', 'mymachine_customerSerialForm')
    }
  } else {
    searchByRequired.value = translatedLabel('searchByRequired', 'mymachine_customerSerialForm')
    isLoading.value = false
    searchErrorMessage.value = ''
  }
}, 300)

function handleClick(item: any): void {
  selectedCustomer.value = ''
  isVisible.value = !isVisible.value
  isLoading.value = !isLoading.value
  nextTick(() => {
    selectedCustomer.value = `${item.accountName} - ${item.firstName} ${item.lastName}${item.city || item.country ? ` - ${item.city ?? ''}, ${item.country ?? ''}` : ''}`
  })
  equipmentsList.all = []
  equipmentsList.displayed = []
  total.value = 0
  dpmmService.searchByMdmId(item.customerMdmId).then((r: any) => {
    equipmentsList.all = r
    total.value = equipmentsList.all.length
    equipmentsList.displayed = equipmentsList.all.slice(0, perPage.value)
    isLoading.value = false
  }).catch(() => {
    isLoading.value = false
  })
}

function selectSerialNumber(): void {
  const sn = {
    serial_number: selectedOption.value
  }
  if (sn.serial_number !== '') {
    loading.value = true
    serialFound.value = ''
    emit('serialNumber', sn)
  } else {
    loading.value = false
    serialFound.value = translatedLabel('serialFound', 'mymachine_customerSerialForm')
  }
}

function closeModal(): void {
  reuseData()
  customerSearchModal.value?.hide()
}

function selectCustomerType(type: string): void {
  reuseData()
  customerType.value = type
}

defineExpose({openModal, loading, closeModal})
</script>

<style lang="scss" scoped>
@import '../../scss/colors';

.customer-search {
  font-family: Helvetica Neue;
}

.search-list {
  list-style-type: none;
  margin: 0;
  padding: 0;
  max-height: 12.5rem;
  overflow-y: auto;
  border: 0.0625rem solid $gray-400;
  border-radius: 0.25rem;
  box-shadow: $box-shadow-sm;
}

.search-list li {
  padding: 0.625rem 0.9375rem;
  border-bottom: 0.0625rem solid $gray-100;
  background-color: $white;
  transition: background-color 0.3s;
}

.search-list li:last-child {
  border-bottom: none;
}

.search-list li:hover {
  background-color: $gray-100;
  cursor: pointer;
}

.no-result {
  margin-top: 0.625rem;
  color: $gray-600;
  font-weight: 700;
}

.datatable {
  gap: 2rem;
  padding-top: 1rem;
  padding-right: 2rem;
  padding-bottom: 2rem;
  padding-left: 2rem;
  background: $gray-100;
}

.registered {
  font-weight: 700;
  font-size: 1rem;
  line-height: 1.5rem;
  letter-spacing: 0.025rem;
  color: $black;
}

.results {
  width: 14rem;
  height: 1.5rem;
  font-weight: 400;
  font-size: 1rem;
  line-height: 1.5rem;
  letter-spacing: 0rem;
  color: $black;
}

.searching-data {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.25rem;
  border-radius: 0.5rem;
  border-width: 0.125rem;
  background: $gray-100;
  border: 0.125rem solid $gray-400;
}

.search-term {
  font-weight: 700;
  font-size: 1.125rem;
  line-height: 1.5rem;
  letter-spacing: 0rem;
  margin-left: 2%;
  margin-bottom: 0rem;
  color: $gray-800;
}

.search-by {
  font-weight: 400;
  font-size: 1rem;
  line-height: 1.5rem;
  letter-spacing: 0rem;
  color: $black;
}

.grid-table-row {
  background: $white;
}

.info-circle {
  font-size: 1.5rem;
}

.serial-found,
.search-by-error {
  color: $red;
}

.search-list :deep(.highlight) {
  background-color: $yellow-600 !important;
  font-weight: bold !important;
}
</style>
